import { z } from 'zod';

/**
 * User-management DTO + form schemas. Mirrors the backend
 * `UserDto` defined in `backend/src/modules/users/model.rs`.
 *
 * Accounts now carry their permission codes directly; `roles` only ever
 * contains `system_admin` (or template roles attached by a super admin).
 */

export const userSchema = z.object({
  user_id: z.string(),
  email: z.string(),
  display_name: z.string(),
  company_id: z.string().nullable().optional(),
  company_display_name: z.string().nullable().optional(),
  facility_id: z.string().nullable().optional(),
  facility_name: z.string().nullable().optional(),
  active_flag: z.boolean(),
  roles: z.array(z.string()),
  permissions: z.array(z.string()),
  created_at: z.string(),
  updated_at: z.string(),
});

export type EfsUser = z.infer<typeof userSchema>;

/**
 * One row of the party permission catalog returned by `GET /api/permissions`.
 * The list is fixed per party type (supplier / processor / platform).
 */
export interface CatalogPermissionDto {
  permission_code: string;
  category: string;
  display_name: string;
  description: string | null;
  sort_order: number;
}

export const PASSWORD_POLICY_MESSAGE =
  'password must be at least 12 characters and include a letter and a digit';

const strongPasswordSchema = z
  .string()
  .min(12, PASSWORD_POLICY_MESSAGE)
  .refine((password) => /[A-Za-z]/.test(password) && /\d/.test(password), PASSWORD_POLICY_MESSAGE);

export const userCreateSchema = z.object({
  email: z.string().email(),
  display_name: z.string().min(1),
  password: strongPasswordSchema,
  company_id: z.string().optional(),
  facility_id: z.string().optional(),
  // No `.min()`: privileged actors may create accounts without a company and
  // grant permissions later (non-empty sets require a company — BE rule). The
  // form always seeds `[]`; `.default([])` would fork zod's input/output types
  // and break the shared `Control<T>` helpers.
  permission_codes: z.array(z.string()),
});

export type UserCreateInput = z.infer<typeof userCreateSchema>;

/**
 * Edit form values. `company_id` / `facility_id` empty string means
 * "clear"; absent means "no change" — the API layer encodes the
 * tri-state when serializing for the BE.
 */
export const userUpdateSchema = z.object({
  display_name: z.string().min(1),
  active_flag: z.boolean(),
  company_id: z.string().optional(),
  facility_id: z.string().optional(),
});

export type UserUpdateInput = z.infer<typeof userUpdateSchema>;

export const passwordResetSchema = z.object({
  new_password: strongPasswordSchema,
});

export type PasswordResetInput = z.infer<typeof passwordResetSchema>;
